import { WebHeader } from "../../../../../shared/ui/WebHeader";
import { requestStepFocus } from "../../components/PageShell";
import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";
import type { PublicProfessionalProfile } from "../../features/profile/operations";
import { ConsentBanner } from "../../components/Consent";
import Footer from "../../components/Footer";

// Shared chrome for every step: a back affordance, the progress indicator, and
// the sticky action bar (actions only since Design's 2026-10-12 ruling; the v1
// kit's BFoot carried a summary). Matches docs/design/lura-book-v1 (BTop,
// BProgress, BFoot).

export function StepTop({
  backTo,
  title,
  sub,
}: {
  backTo: string;
  title: string;
  sub?: string;
}) {
  const { t, locale } = useTranslation();
  return (
    <header className="b-top b3-account-top">
      <WebHeader as="div" locale={locale} />
      <Link className="b-iconbtn" to={backTo} aria-label={t("flow.back")}>
        <BackChevron />
      </Link>
      <div className="b-top-t">
        <b>{title}</b>
        {sub ? <span>{sub}</span> : null}
      </div>
    </header>
  );
}

type StepLeadProps = { backTo?: string; step: number; of: number; v3?: boolean; label?: string };

/**
 * The step header's lead under the wordmark: Back (or the entry label) and
 * the step count, then the progress rule. A FlowFrame's `lead` (c6): the
 * shell draws the wordmark once for the whole flow.
 */
export function StepLeadBar({ backTo, step, of, v3 = false, label }: StepLeadProps) {
  const { t } = useTranslation();
  return (
    <>
      <div className="b2-stepbar">
        {backTo ? <Link className="b2-stepback" to={backTo} onClick={() => {
          // Only to another step: the account-gated service step's Back leaves
          // the flow for the profile, where no step heading will arrive.
          if (backTo.includes("/book/")) requestStepFocus();
        }}>
          <BackChevron />{t("flow.back")}
        </Link> : <span className="b3-entry-label">{label ?? t("flow.service.heading")}</span>}
        <span className="b2-stepcount">{t("flow.progress", { step, of })}</span>
      </div>
      {v3 ? <StepProgress step={step} of={of} /> : null}
    </>
  );
}

/**
 * Progress through the flow.
 *
 * `aria-label` carries the same information the bar shows visually — a
 * decorative row of filled divs tells a screen-reader user nothing about where
 * they are in a multi-step process.
 */
export function StepProgress({ step, of }: { step: number; of: number }) {
  const { t } = useTranslation();
  return (
    <div
      className="b-progress"
      role="group"
      aria-label={t("flow.progress", { step, of })}
    >
      {Array.from({ length: of }, (_, index) => (
        <i key={index} className={index < step ? "is-on" : undefined} aria-hidden="true" />
      ))}
    </div>
  );
}

/**
 * The booking action bar: the primary action (and a secondary one, where a
 * step has it), plus Google's reCAPTCHA line above a protected action. NO
 * SUMMARY ROW (Design, 2026-10-12): every step already shows what was picked
 * in the page, and on a phone a summary line made the pinned bar cover
 * content. The v3 mockup has none on any booking screen.
 */
export function StepFoot({ children }: { children: React.ReactNode }) {
  return <div className="b-foot">{children}</div>;
}

/**
 * The scrolling meta footer every step carries.
 *
 * WHY IT EXISTS AT ALL. `persistentControl` is assigned by
 * `ConsentFooterItems`, and the five step views mount no `Footer` — so through
 * the entire booking flow there was no connected Cookie settings control. The
 * consequences were all silent: answering the banner called `.focus()` on a ref
 * that had never been assigned, which is a no-op, so focus stayed on `<body>`;
 * a settings save had nowhere to return focus to; and BC-06 could not render,
 * because `ConsentStatus` was not on the page either. Every test that looked at
 * this mounted the footer helper, so every test passed.
 *
 * WHY IT IS NOT IN `StepFoot`. `.b-foot` is the sticky booking action bar. It
 * holds the primary action, it is pinned over the content, and
 * legal links do not belong on top of the primary action on all five steps.
 * This goes at the end of `.b-scroll` instead, where it scrolls with the page.
 * ONE RECORDED EXCEPTION: Google's reCAPTCHA line (shared/ui/RecaptchaNotice)
 * sits in the bar above Review's and the code step's primary action, because
 * counsel requires it beside the action (2026-10-09).
 *
 * NO `<nav>` and no label. The group is two controls, not site navigation, and
 * an `aria-label` is visitor-facing copy — which is Product's and Legal's to
 * approve, not something to invent here. `Footer` keeps its own nav because it
 * carries four destinations.
 */
export function StepMeta({ secondary }: { secondary?: React.ReactNode } = {}) {
  return (
    <div className="b-stepmeta">
      {/* A step's secondary action (the service step's Sign in), first in the
          scrolling footer area, after the content and before the footer
          links: v3's view.secondary in .scroll-footer. Never in the pinned
          bar (Design, 2026-10-12). */}
      {secondary ? <div className="b3-step-secondary">{secondary}</div> : null}
      <ConsentBanner />
      <Footer as="div" />
    </div>
  );
}

/** The price line every step repeats, so it reads identically throughout. */
export function serviceSummary(
  profile: PublicProfessionalProfile,
  serviceId: string | null,
  fmt: { money(cents: number): string; duration(minutes: number): string }
): string | null {
  const service = profile.services.find((candidate) => candidate.id === serviceId);
  if (!service) return null;
  return `${service.name} · ${fmt.duration(service.durationMinutes)}`;
}

function BackChevron() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>;
}
